import { QK } from '@api/queryKeys';
import { isDocumentAnalysisSourceRefusal } from '@entities/document';
import { isAuthErrorHandledByInterceptor } from '@lib/api/serverStateErrorHandler';
import { emitToast } from '@lib/api/toastBridge';
import { documentKeys, isDocumentListKey, meetingKeys, requestKeys } from '@shared/api/query-keys';
import { invalidateDocumentEmbeddingViews } from '@shared/api/documentEmbeddingViews';
import {
  AddDocumentCommentDto,
  AddDocumentDto,
  DocumentAnnotation,
  DocumentEntity,
  DocumentEditorResponse,
  DocumentMetaType,
  DocumentSearchParams,
  EditDocumentDto,
  InviteDocumentGuestDto,
  InviteDocumentGuestResponse,
  ReplaceDocumentData,
  SignDocumentDto,
  UpdateEditorDocumentMetadataDto,
  UpdateDocumentSignaturesBody,
} from '@api/types/document';
import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { documentsApi } from './documents';
import { documentQueries } from './queries';

const mergeCreatedDocuments = (
  currentDocuments: DocumentEntity[] | undefined,
  createdDocuments: DocumentEntity[],
) => {
  if (!createdDocuments.length) return currentDocuments;
  if (!currentDocuments) return createdDocuments;

  const createdDocumentIds = new Set(createdDocuments.map((document) => document.id));
  const existingDocuments = currentDocuments.filter(
    (document) => !createdDocumentIds.has(document.id),
  );

  return [...createdDocuments, ...existingDocuments];
};

export const useGetDocument = (id: string, documentId: string, shouldSync?: boolean) => {
  return useQuery({
    ...documentQueries.detail(id, documentId),
    enabled: Boolean(id && documentId),
    refetchInterval: shouldSync ? 5000 : false,
  });
};

export const useGetDocumentEditor = (auditId: string, documentId: string) => {
  return useQuery({
    ...documentQueries.editor(auditId, documentId),
    enabled: Boolean(auditId && documentId),
    refetchInterval: (query) => {
      const data = query.state.data as DocumentEditorResponse | undefined;
      const hasInProgressSigners = Boolean(
        data?.document?.signers?.some((signer) => signer.status === 'in_progress'),
      );
      return hasInProgressSigners ? 5000 : false;
    },
  });
};

export const useCreateAnnotation = () => {
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: Partial<DocumentAnnotation>;
    }) => documentsApi.createAnnotation(auditId, documentId, data),
  });
};

export const useUpdateAnnotation = () => {
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      annotationId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      annotationId: string | number;
      data: Partial<DocumentAnnotation>;
    }) => documentsApi.updateAnnotation(auditId, documentId, annotationId, data),
  });
};

export const useDeleteAnnotation = () => {
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      annotationId,
    }: {
      auditId: string;
      documentId: string | number;
      annotationId: string | number;
    }) => documentsApi.deleteAnnotation(auditId, documentId, annotationId),
  });
};

export const useBulkAnnotations = () => {
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: {
        create?: Array<Partial<DocumentAnnotation>>;
        update?: Array<Partial<DocumentAnnotation>>;
        remove?: Array<number>;
      };
    }) => documentsApi.bulkAnnotations(auditId, documentId, data),
  });
};

export const useAnnotationHistory = (auditId: string, documentId: string, enabled = true) => {
  return useQuery({
    ...documentQueries.annotationHistory(auditId, documentId),
    enabled: enabled && Boolean(auditId && documentId),
  });
};

export const useUpdateWorkPerformed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      workPerformed,
    }: {
      auditId: string;
      documentId: string | number;
      workPerformed: string;
    }) => documentsApi.updateWorkPerformed(auditId, documentId, workPerformed),
    onSuccess: (_result, { auditId, documentId }) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.editor(auditId, documentId),
      });
    },
  });
};

export const useUpdateEditorMetadata = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: UpdateEditorDocumentMetadataDto;
    }) => documentsApi.updateEditorMetadata(auditId, documentId, data),
    // Optimistically reflect document-type tag changes so the chips update
    // instantly instead of waiting for the round-trip + refetch. Other fields
    // are reconciled by the `onSettled` refetch below.
    onMutate: async ({ auditId, documentId, data }) => {
      const editorKey = documentKeys.editor(auditId, documentId);
      // Only the document-type tags are patched here; skip the rest.
      if (data.metaTypeIds === undefined) {
        return { previous: undefined, editorKey };
      }
      await queryClient.cancelQueries({ queryKey: editorKey });
      const previous = queryClient.getQueryData<DocumentEditorResponse>(editorKey);

      if (previous?.document) {
        // Resolve ids to full meta types, preferring the loaded options list and
        // falling back to types already on the document (e.g. a just-created one).
        const optionMap = new Map<number, DocumentMetaType>();
        queryClient
          .getQueryData<{ documentTypes: DocumentMetaType[] }>(documentKeys.metaTypes(auditId))
          ?.documentTypes.forEach((type) => optionMap.set(type.id, type));
        previous.document.metaTypes?.forEach((type) => optionMap.set(type.id, type));

        const nextMetaTypes = data.metaTypeIds
          .map((id) => optionMap.get(Number(id)))
          .filter((type): type is DocumentMetaType => Boolean(type));

        queryClient.setQueryData<DocumentEditorResponse>(editorKey, {
          ...previous,
          document: { ...previous.document, metaTypes: nextMetaTypes },
        });
      }
      return { previous, editorKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous && context.editorKey) {
        queryClient.setQueryData(context.editorKey, context.previous);
      }
    },
    onSettled: (_result, _error, { auditId, documentId }) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.editor(auditId, documentId),
      });
      queryClient.invalidateQueries({ queryKey: documentKeys.lists(auditId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(auditId, documentId) });
    },
  });
};

export const useCreateAnnotationGroup = () => {
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.createAnnotationGroup(auditId, documentId),
  });
};

export const useDocumentTypes = (auditId: string) => {
  return useQuery({
    ...documentQueries.metaTypes(auditId),
    enabled: Boolean(auditId),
  });
};

export const useCreateDocumentType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      title,
      color,
    }: {
      auditId: string;
      title: string;
      color?: string | null;
    }) => documentsApi.createDocumentType(auditId, title, color),
    onSuccess: (_result, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.metaTypes(auditId) });
    },
  });
};

export const useUpdateDocumentType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      documentTypeId,
      data,
    }: {
      auditId: string;
      documentTypeId: string | number;
      data: { title?: string; color?: string | null };
    }) => documentsApi.updateDocumentType(auditId, documentTypeId, data),
    onSuccess: (_result, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.metaTypes(auditId) });
    },
  });
};

export const useDeleteDocumentType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      documentTypeId,
    }: {
      auditId: string;
      documentTypeId: string | number;
    }) => documentsApi.deleteDocumentType(auditId, documentTypeId),
    onSuccess: (_result, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.metaTypes(auditId) });
    },
  });
};

export const useDocumentsWithBacklinks = (
  auditId: string,
  params?: DocumentSearchParams,
  options?: { enabled?: boolean; refetchOnMount?: boolean | 'always' },
) => {
  return useQuery({
    ...documentQueries.list(auditId, params),
    enabled: (options?.enabled ?? true) && Boolean(auditId),
    ...(options?.refetchOnMount !== undefined && { refetchOnMount: options.refetchOnMount }),
    // This is the canonical document-list fetch (used by ~10 pickers/pages) and the
    // backlink computation behind it is expensive, so we must NOT refetch on every mount.
    // Inherit the global defaults (staleTime 5m / gcTime 10m): navigating between pages
    // serves the cached list instead of re-hitting this endpoint. Freshness is guaranteed
    // by explicit invalidation — `useSyncAuditData` on remote changes and the document
    // mutations in this file (add/edit/delete/replace…) — not by a 0 staleTime.
    // ONE EXCEPTION: the Documents page passes `refetchOnMount: 'always'`. Its rows show
    // backlink counts, and a meeting attach or a mention written through collab never
    // invalidates this list, so the page re-reads it in the background on every visit
    // while the cached rows render at once. The pickers keep the cache.
  });
};

const toHitIds = (documents: DocumentEntity[]) => new Set(documents.map(({ id }) => id));

/**
 * Which of the audit's documents match `term` on the server — by name OR by comment text —
 * as an id set. Asks with `backlinks: 0`, so the server skips the citation scan: the rows to
 * render, and their backlinks, come from the cached full list, never from this read. The
 * cached data stays a `DocumentEntity[]` under `lists(auditId)` (the mutations write through
 * every entry under that prefix); only `select` turns it into ids.
 */
export const useDocumentSearchHits = (
  auditId: string,
  term: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...documentQueries.list(auditId, { search: term, backlinks: 0 }),
    enabled: (options?.enabled ?? true) && Boolean(auditId) && term !== '',
    select: toHitIds,
  });
};

export const useDeletedDocuments = (
  auditId: string,
  params?: DocumentSearchParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...documentQueries.deletedList(auditId, params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useAddDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ auditId, document }: { auditId: string; document: AddDocumentDto }) =>
      documentsApi.addDocument(auditId, document),
    onSuccess: (_result, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists(auditId) });
    },
  });
};

export const useAddMultipleDocuments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ auditId, data }: { auditId: string; data: FormData }) =>
      documentsApi.addMultipleDocuments(auditId, data),
    onSuccess: (createdDocuments, { auditId }) => {
      queryClient.setQueriesData<DocumentEntity[]>(
        {
          queryKey: documentKeys.lists(auditId),
          // Lists only: the prefix also holds the meta-types read (#875).
          predicate: ({ queryKey }) => isDocumentListKey(queryKey, auditId),
        },
        (currentDocuments) => mergeCreatedDocuments(currentDocuments, createdDocuments),
      );
    },
  });
};

export const useUpdateDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      document,
    }: {
      auditId: string;
      documentId: string | number;
      document: EditDocumentDto;
    }) => documentsApi.updateDocument(auditId, documentId, document),
    onSuccess: (_result, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists(auditId) });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.deleteDocument(auditId, documentId),
    onSuccess: (_result, { auditId }) => {
      // A deleted document is embedded in meetings (`meeting.files`) and request
      // response documents, so those caches go stale alongside the documents list.
      // Scope to this audit and only refetch mounted queries (§5.2).
      queryClient.invalidateQueries({
        queryKey: documentKeys.lists(auditId),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: meetingKeys.lists(auditId),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: meetingKeys.details(auditId),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: QK.requests(auditId),
        refetchType: 'active',
      });
    },
  });
};

type ReplaceDocumentVariables = {
  auditId: string;
  documentId: string | number;
  data: ReplaceDocumentData;
};

export const useReplaceDocument = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dashboard', { keyPrefix: 'documents' });
  return useMutation({
    mutationKey: documentKeys.replaceWrite(),
    mutationFn: ({ auditId, documentId, data }: ReplaceDocumentVariables) =>
      documentsApi.replaceDocument(auditId, documentId, data),
    /**
     * The server's own sentence, as a toast. Replacing FROM another document is refused
     * 409 while that source is cited by a Finding's reason (0.54.0) — the sentence names
     * the remedy ("remove that reference before replacing"), and swallowing it left the
     * auditor with a replace that silently never happened. The one exception is a
     * document an import was made from: since #908 a client's upload can be one, so
     * `onError` says that refusal in the user's language instead. A swap is refused when
     * EITHER end is one (its source is deleted), and the refusal does not say which.
     */
    meta: { showErrorToast: true, suppressErrorToastFor: isDocumentAnalysisSourceRefusal },
    onError: (error, { data }) => {
      if (isAuthErrorHandledByInterceptor(error)) return;
      if (!isDocumentAnalysisSourceRefusal(error)) return;
      emitToast({
        type: 'error',
        message: t(
          data.sourceId != null
            ? 'replace_document_analysis_source_swap'
            : 'replace_document_analysis_source',
        ),
      });
    },
    onSuccess: (answer, { auditId, data }) => {
      // A swap hard-deletes its source, so nothing will ever read that document again.
      // An older API names no `removedDocumentId`; the source it was sent is the same id.
      const removedDocumentId = answer.removedDocumentId ?? data.sourceId;
      if (removedDocumentId != null) {
        // `detail` is the prefix of the editor and annotation-history keys too.
        queryClient.removeQueries({ queryKey: documentKeys.detail(auditId, removedDocumentId) });
      }
    },
    /**
     * On SETTLE, not on success: a file replace can still fail after its write has
     * committed, and a refused one changes nothing, so the refetch is what decides. The
     * server's `document` / `request-container` events refetch the same reads; they are
     * best-effort, so this stays the guaranteed path, and the actor's tab refetching twice
     * is expected.
     */
    onSettled: (_answer, _error, { auditId, documentId }) => {
      // A document is single-audit, so scope every invalidation to this audit and only
      // refetch mounted queries (§5.2). Not awaited: the caller applies the answer at
      // once and the refetch replaces it when it lands.
      const refetchActive = (queryKey: QueryKey) => {
        void queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
      };
      refetchActive(documentKeys.lists(auditId));
      // The target's own reads, the editor included (`detail` is its prefix).
      refetchActive(documentKeys.detail(auditId, documentId));
      refetchActive(meetingKeys.lists(auditId));
      refetchActive(QK.requests(auditId));
      // A request block reads its file rows from the request read, not the documents
      // list, so the request reads must follow the replace too (#609).
      refetchActive(requestKeys.containerRoot(auditId));
      refetchActive(requestKeys.blockRoot(auditId));
      refetchActive(requestKeys.list(auditId));
      // The status board, open meeting details and the meeting picker embed the replaced
      // (and, on a swap, the deleted) document — the same set the socket echo refreshes. A
      // read already in flight may predate the commit, so it is restarted, not joined.
      invalidateDocumentEmbeddingViews(queryClient, auditId, { restartInFlight: true });
    },
  });
};

/**
 * Does this pending replace touch the document: as its target, or as the source a swap
 * deletes? Ids are compared as strings because callers hold them as numbers or as route
 * params. A document id is unique across audits, so the id alone is the key.
 */
const replaceTouches = (
  variables: ReplaceDocumentVariables | undefined,
  documentId: string | number,
): boolean =>
  variables != null &&
  (String(variables.documentId) === String(documentId) ||
    (variables.data.sourceId != null && String(variables.data.sourceId) === String(documentId)));

const pendingReplaceFilters = {
  mutationKey: documentKeys.replaceWrite(),
  status: 'pending',
} as const;

/**
 * Is a replace that touches THIS document in flight, from any surface of this tab (#609)?
 *
 * The Replace popover closes on the pick, and nothing on the row changes until the server
 * answers, so without this the row looks idle and a second Replace can start on the same
 * document. Read from the mutation cache, so every surface that shows the document agrees
 * without keeping a flag of its own.
 *
 * The pending replaces are selected without reference to `documentId` and matched at
 * render: a filter closing over the id would be re-read only on the next mutation-cache
 * event, so an id that arrives after mount (the editor header while its document loads)
 * would read idle through a replace already running.
 */
export const useIsReplacingDocument = (documentId: string | number | undefined): boolean => {
  const pending = useMutationState({
    filters: pendingReplaceFilters,
    select: (mutation) => mutation.state.variables as ReplaceDocumentVariables | undefined,
  });
  return documentId != null && pending.some((variables) => replaceTouches(variables, documentId));
};

/** The same question outside render, for a write path that must refuse a second replace. */
export const isReplacingDocument = (
  queryClient: QueryClient,
  documentId: string | number,
): boolean =>
  queryClient.isMutating({
    ...pendingReplaceFilters,
    predicate: (mutation) =>
      replaceTouches(mutation.state.variables as ReplaceDocumentVariables | undefined, documentId),
  }) > 0;

export const useEditDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: EditDocumentDto;
    }) => documentsApi.editDocument(auditId, documentId, data),
    onSuccess: (_result, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists(auditId) });
    },
    // The inline rename's only feedback for a refusal; the field reverts on its own.
    meta: { showErrorToast: true },
  });
};

export const useUpdateDocumentSignatures = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: UpdateDocumentSignaturesBody;
    }) => documentsApi.updateDocumentSignatures(auditId, documentId, data),
    onSuccess: async (_result, { auditId, documentId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: documentKeys.lists(auditId) }),
        queryClient.invalidateQueries({ queryKey: documentKeys.detail(auditId, documentId) }),
        queryClient.invalidateQueries({
          queryKey: documentKeys.editor(auditId, documentId),
        }),
      ]);
    },
  });
};

export const useSignDocument = () => {
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: SignDocumentDto;
    }) => documentsApi.signDocument(auditId, documentId, data),
  });
};

export const useDownloadDocument = () => {
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.downloadDocument(auditId, documentId),
  });
};

export const useAddDocumentComments = () => {
  return useMutation({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: AddDocumentCommentDto;
    }) => documentsApi.addDocumentComments(auditId, documentId, data),
  });
};

export const useInviteDocumentGuest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InviteDocumentGuestResponse,
    AxiosError<{ message: string }>,
    { auditId: string; documentId: string | number; data: InviteDocumentGuestDto }
  >({
    mutationFn: ({
      auditId,
      documentId,
      data,
    }: {
      auditId: string;
      documentId: string | number;
      data: InviteDocumentGuestDto;
    }) => documentsApi.inviteDocumentGuest(auditId, documentId, data),
    onSuccess: async (data, { auditId, documentId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: documentKeys.detail(auditId, documentId) }),
        queryClient.invalidateQueries({ queryKey: documentKeys.editor(auditId, documentId) }),
        queryClient.invalidateQueries({ queryKey: documentKeys.lists(auditId) }),
      ]);
    },
  });
};

export const useRestoreDocument = () => {
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.restoreDocument(auditId, documentId),
  });
};

export const useSyncDocument = () => {
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.syncDocument(auditId, documentId),
  });
};

export const useVerifySwisscom = () => {
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.verifySwisscom(auditId, documentId),
  });
};

/** @deprecated Use `useBexioExport` from `@api/bexio` — same mutation, scoped to Bexio. */
export const useExportDocument = () => {
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.exportDocument(auditId, documentId),
  });
};

/** @deprecated Use `useBexioRefetch` from `@api/bexio` — same mutation, scoped to Bexio. */
export const useRefetchDocument = () => {
  return useMutation({
    mutationFn: ({ auditId, documentId }: { auditId: string; documentId: string | number }) =>
      documentsApi.refetchDocument(auditId, documentId),
  });
};
